import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Settings,
  DollarSign,
  Clock,
  Calendar,
  Save,
  RotateCcw,
  ChevronLeft,
  Copy,
  ChevronDown,
  ChevronUp,
  Archive
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const LaneSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [_config, setConfig] = useState(null);
  const [_resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newLaneName, setNewLaneName] = useState('');
  const [addingLane, setAddingLane] = useState(false);
  
  // Global settings
  const [settings, setSettings] = useState({
    advance_booking_days: 7,
    allow_recurring: true,
    cancellation_free_hours: 24,
    cancellation_credit_hours: 6,
    auto_charge_returning_customers: true,
  });
  
  // Lane configs
  const [lanes, setLanes] = useState([]);

  // Business locations (for assigning each lane to a site).
  const [locations, setLocations] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  
  // Expanded hours state per lane
  const [expandedHours, setExpandedHours] = useState({});
  
  // Days of week for custom hours
  const DAYS_OF_WEEK = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  useEffect(() => {
    fetchConfig();
    api.get('/locations/active').then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/lanes/config');
      setConfig(response.data);
      setSettings({
        advance_booking_days: response.data.advance_booking_days || 7,
        allow_recurring: response.data.allow_recurring !== false,
        cancellation_free_hours: response.data.cancellation_free_hours || 24,
        cancellation_credit_hours: response.data.cancellation_credit_hours || 6,
        auto_charge_returning_customers: response.data.auto_charge_returning_customers !== false,
      });
      setLanes(response.data.lanes || []);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load lane configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/lanes/config/settings', settings);
      toast.success('Global settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLane = async (lane) => {
    setSaving(true);
    try {
      await api.put(`/lanes/config/lane/${lane.id}`, {
        name: lane.name,
        is_active: lane.is_active,
        location_id: lane.location_id || null,
        pricing: lane.pricing,
        min_duration: lane.min_duration,
        max_duration: lane.max_duration,
        use_custom_hours: lane.use_custom_hours || false,
        custom_hours: lane.custom_hours || null
      });
      toast.success(`${lane.name} configuration saved`);
    } catch (error) {
      toast.error('Failed to save lane configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyBusinessHours = async (laneId) => {
    setSaving(true);
    try {
      const response = await api.post(`/lanes/config/lane/${laneId}/copy-business-hours`);
      toast.success('Business hours copied to lane');
      // Update local state
      const laneIndex = lanes.findIndex(l => l.id === laneId);
      if (laneIndex !== -1) {
        const newLanes = [...lanes];
        newLanes[laneIndex].use_custom_hours = true;
        newLanes[laneIndex].custom_hours = response.data.custom_hours;
        setLanes(newLanes);
        setExpandedHours(prev => ({ ...prev, [laneId]: true }));
      }
    } catch (error) {
      toast.error('Failed to copy business hours');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpandedHours = (laneId) => {
    setExpandedHours(prev => ({ ...prev, [laneId]: !prev[laneId] }));
  };

  const updateLaneCustomHours = (laneIndex, dayKey, field, value) => {
    const newLanes = [...lanes];
    const lane = newLanes[laneIndex];
    
    // Initialize custom_hours if not present
    if (!lane.custom_hours) {
      lane.custom_hours = DAYS_OF_WEEK.map(d => ({
        day: d.key,
        is_open: true,
        open_time: '07:00',
        close_time: '22:00'
      }));
    }
    
    const dayIndex = lane.custom_hours.findIndex(h => h.day === dayKey);
    if (dayIndex !== -1) {
      lane.custom_hours[dayIndex][field] = value;
    }
    
    setLanes(newLanes);
  };

  const getLaneHoursForDay = (lane, dayKey) => {
    if (!lane.custom_hours) {
      return { is_open: true, open_time: '07:00', close_time: '22:00' };
    }
    return lane.custom_hours.find(h => h.day === dayKey) || { is_open: true, open_time: '07:00', close_time: '22:00' };
  };

  const handleResetDefaults = async () => {
    setResetDialogOpen(true);
  };

  const _confirmResetDefaults = async () => {
    try {
      await api.post('/lanes/config/reset');
      toast.success('Configurations reset to defaults');
      setResetDialogOpen(false);
      fetchConfig();
    } catch (error) {
      toast.error('Failed to reset configurations');
    }
  };

  const updateLanePricing = (laneIndex, duration, value) => {
    const newLanes = [...lanes];
    newLanes[laneIndex].pricing[duration] = parseFloat(value) || 0;
    setLanes(newLanes);
  };

  const updateLaneField = (laneIndex, field, value) => {
    const newLanes = [...lanes];
    newLanes[laneIndex][field] = value;
    setLanes(newLanes);
  };

  const handleAddLane = async () => {
    if (!newLaneName.trim()) { toast.error('Lane name is required'); return; }
    setAddingLane(true);
    try {
      await api.post('/lanes/config/lanes', { name: newLaneName.trim() });
      toast.success(`${newLaneName.trim()} created`);
      setNewLaneName('');
      fetchConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create lane'); }
    finally { setAddingLane(false); }
  };

  const handleDeleteLane = async (lane) => {
    if (!window.confirm(`Delete "${lane.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/lanes/config/lanes/${lane.id}`);
      toast.success(`${lane.name} deleted`);
      fetchConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete lane'); }
  };

  const handleArchiveLane = async (lane, archived) => {
    const verb = archived ? 'Archive' : 'Restore';
    if (!window.confirm(`${verb} "${lane.name}"?${archived ? ' It will be hidden from booking and kept for its history.' : ' It will stay inactive until you re-activate it.'}`)) return;
    try {
      await api.post(`/lanes/config/lanes/${lane.id}/archive?archived=${archived}`);
      toast.success(`${lane.name} ${archived ? 'archived' : 'restored'}`);
      fetchConfig();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${verb.toLowerCase()} lane`); }
  };

  if (loading) {
    return (
      <DashboardLayout title="Lane Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Lane Settings">
      <div className="space-y-6" data-testid="lane-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/lanes">
              <Button variant="outline" size="icon">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Lane Configuration</h2>
              <p className="text-slate-500 text-sm">Manage pricing, durations, and booking policies</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleResetDefaults}
            className="text-red-600 hover:bg-red-50"
            data-testid="reset-defaults-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Defaults
          </Button>
        </div>

        {/* Global Settings */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-500" />
              Global Booking Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  Advance Booking Days
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={settings.advance_booking_days}
                  onChange={(e) => setSettings({...settings, advance_booking_days: parseInt(e.target.value) || 7})}
                  data-testid="advance-days-input"
                />
                <p className="text-xs text-slate-500">How far in advance customers can book</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Free Cancellation (hours)
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="72"
                  value={settings.cancellation_free_hours}
                  onChange={(e) => setSettings({...settings, cancellation_free_hours: parseInt(e.target.value) || 24})}
                  data-testid="free-cancel-input"
                />
                <p className="text-xs text-slate-500">Free cancellation if cancelled before this</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Credit Cancellation (hours)
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="24"
                  value={settings.cancellation_credit_hours}
                  onChange={(e) => setSettings({...settings, cancellation_credit_hours: parseInt(e.target.value) || 6})}
                  data-testid="credit-cancel-input"
                />
                <p className="text-xs text-slate-500">Account credit if cancelled before this</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Allow Recurring Bookings
                </Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={settings.allow_recurring}
                    onCheckedChange={(checked) => setSettings({...settings, allow_recurring: checked})}
                    data-testid="allow-recurring-switch"
                  />
                  <span className="text-sm text-slate-600">
                    {settings.allow_recurring ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label className="flex items-center gap-2">
                  Quick-book Returning Customers (Saved Card)
                </Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={settings.auto_charge_returning_customers}
                    onCheckedChange={(checked) => setSettings({...settings, auto_charge_returning_customers: checked})}
                    data-testid="auto-charge-returning-customers-switch"
                  />
                  <span className="text-sm text-slate-600">
                    {settings.auto_charge_returning_customers ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  When enabled, returning customers who saved a card on a prior booking can skip the Stripe Checkout redirect — their card on file is charged off-session for a one-click &quot;Pay &amp; Book&quot;. Recommended for walk-in lanes.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveSettings} 
                disabled={saving}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="save-settings-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Global Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Manage Lanes */}
        <Card className="border border-slate-200" data-testid="manage-lanes-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-purple-500" />
              Manage Lanes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center gap-3 mb-4">
              <Input
                placeholder="New lane name (e.g. Lane 5)"
                value={newLaneName}
                onChange={(e) => setNewLaneName(e.target.value)}
                className="max-w-xs"
                data-testid="new-lane-name"
                onKeyDown={(e) => e.key === 'Enter' && handleAddLane()}
              />
              <Button onClick={handleAddLane} disabled={addingLane || !newLaneName.trim()} data-testid="add-lane-btn">
                {addingLane ? 'Adding...' : 'Add Lane'}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {lanes.filter(l => showArchived ? true : !l.is_archived).map(lane => (
                <div key={lane.id} className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${lane.is_archived ? 'bg-amber-50 border-amber-200 opacity-80' : 'bg-slate-50'}`}>
                  <span className="text-sm font-medium">{lane.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${lane.is_archived ? 'bg-amber-100 text-amber-700' : lane.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {lane.is_archived ? 'Archived' : lane.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {lane.is_archived ? (
                    <button onClick={() => handleArchiveLane(lane, false)} className="text-xs text-blue-600 hover:text-blue-800 ml-1" data-testid={`restore-lane-${lane.id}`} title="Restore lane">
                      Restore
                    </button>
                  ) : (
                    <>
                      <button onClick={() => handleArchiveLane(lane, true)} className="text-slate-400 hover:text-amber-600 ml-1" data-testid={`archive-lane-${lane.id}`} title="Archive lane">
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleDeleteLane(lane)} className="text-red-400 hover:text-red-600" data-testid={`delete-lane-${lane.id}`} title="Delete lane">
                        &times;
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-slate-500">Add, archive or remove lanes. Lanes with future or paid bookings cannot be deleted — archive them instead to keep their history. Configure pricing and hours below.</p>
              <label className="flex items-center gap-2 text-xs text-slate-600 whitespace-nowrap cursor-pointer" data-testid="show-archived-lanes-toggle">
                <Switch checked={showArchived} onCheckedChange={setShowArchived} />
                Show archived
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Lane Configurations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {lanes.map((lane, index) => (
            (!showArchived && lane.is_archived) ? null : (
            <Card key={lane.id} className={`border ${lane.is_archived ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'}`} data-testid={`lane-config-${lane.id}`}>
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-500" />
                    {lane.name}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Active</Label>
                    <Switch
                      checked={lane.is_active}
                      onCheckedChange={(checked) => updateLaneField(index, 'is_active', checked)}
                      data-testid={`lane-${lane.id}-active`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 lg:p-6">
                <div className="space-y-4">
                  {/* Lane Name */}
                  <div className="space-y-2">
                    <Label>Display Name</Label>
                    <Input
                      value={lane.name}
                      onChange={(e) => updateLaneField(index, 'name', e.target.value)}
                      data-testid={`lane-${lane.id}-name`}
                    />
                  </div>

                  {/* Location assignment — drives location-scoped Billing Overview */}
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <select
                      value={lane.location_id || ''}
                      onChange={(e) => updateLaneField(index, 'location_id', e.target.value || null)}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      data-testid={`lane-${lane.id}-location`}
                    >
                      <option value="">Unassigned (shows under all locations)</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-400">
                      Assign the site this lane belongs to so its bookings are counted under the right location in Billing Overview.
                    </p>
                  </div>
                  
                  {/* Pricing */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Pricing</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">30 minutes</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-7"
                            value={lane.pricing?.duration_30 || 0}
                            onChange={(e) => updateLanePricing(index, 'duration_30', e.target.value)}
                            data-testid={`lane-${lane.id}-price-30`}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">1 hour</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-7"
                            value={lane.pricing?.duration_60 || 0}
                            onChange={(e) => updateLanePricing(index, 'duration_60', e.target.value)}
                            data-testid={`lane-${lane.id}-price-60`}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">1.5 hours</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-7"
                            value={lane.pricing?.duration_90 || 0}
                            onChange={(e) => updateLanePricing(index, 'duration_90', e.target.value)}
                            data-testid={`lane-${lane.id}-price-90`}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">2 hours</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-7"
                            value={lane.pricing?.duration_120 || 0}
                            onChange={(e) => updateLanePricing(index, 'duration_120', e.target.value)}
                            data-testid={`lane-${lane.id}-price-120`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Duration Limits */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Min Duration (min)</Label>
                      <Input
                        type="number"
                        min="30"
                        step="30"
                        value={lane.min_duration || 30}
                        onChange={(e) => updateLaneField(index, 'min_duration', parseInt(e.target.value) || 30)}
                        data-testid={`lane-${lane.id}-min-duration`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Max Duration (min)</Label>
                      <Input
                        type="number"
                        min="30"
                        step="30"
                        value={lane.max_duration || 120}
                        onChange={(e) => updateLaneField(index, 'max_duration', parseInt(e.target.value) || 120)}
                        data-testid={`lane-${lane.id}-max-duration`}
                      />
                    </div>
                  </div>
                  
                  {/* Custom Operating Hours */}
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        <Label className="text-sm font-medium">Custom Operating Hours</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`custom-hours-${lane.id}`}
                          checked={lane.use_custom_hours || false}
                          onCheckedChange={(checked) => updateLaneField(index, 'use_custom_hours', checked)}
                          data-testid={`lane-${lane.id}-use-custom-hours`}
                        />
                        <Label htmlFor={`custom-hours-${lane.id}`} className="text-xs text-slate-500 cursor-pointer">
                          Enable
                        </Label>
                      </div>
                    </div>
                    
                    {lane.use_custom_hours && (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyBusinessHours(lane.id)}
                            disabled={saving}
                            className="text-xs"
                            data-testid={`lane-${lane.id}-copy-hours`}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy from Business Hours
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpandedHours(lane.id)}
                            className="text-xs"
                            data-testid={`lane-${lane.id}-toggle-hours`}
                          >
                            {expandedHours[lane.id] ? (
                              <>
                                <ChevronUp className="h-3 w-3 mr-1" />
                                Hide Hours
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-3 w-3 mr-1" />
                                Show Hours
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {expandedHours[lane.id] && (
                          <div className="space-y-2 bg-slate-50 rounded-lg p-3">
                            {DAYS_OF_WEEK.map(day => {
                              const hours = getLaneHoursForDay(lane, day.key);
                              return (
                                <div key={day.key} className="flex items-center gap-3" data-testid={`lane-${lane.id}-hours-${day.key}`}>
                                  <div className="w-24">
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        id={`${lane.id}-${day.key}-open`}
                                        checked={hours.is_open}
                                        onCheckedChange={(checked) => updateLaneCustomHours(index, day.key, 'is_open', checked)}
                                      />
                                      <Label htmlFor={`${lane.id}-${day.key}-open`} className="text-xs font-medium cursor-pointer">
                                        {day.label.substring(0, 3)}
                                      </Label>
                                    </div>
                                  </div>
                                  {hours.is_open ? (
                                    <div className="flex items-center gap-2 flex-1">
                                      <Input
                                        type="time"
                                        value={hours.open_time || '07:00'}
                                        onChange={(e) => updateLaneCustomHours(index, day.key, 'open_time', e.target.value)}
                                        className="h-8 text-xs w-28"
                                      />
                                      <span className="text-xs text-slate-400">to</span>
                                      <Input
                                        type="time"
                                        value={hours.close_time || '22:00'}
                                        onChange={(e) => updateLaneCustomHours(index, day.key, 'close_time', e.target.value)}
                                        className="h-8 text-xs w-28"
                                      />
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">Closed</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {!expandedHours[lane.id] && lane.custom_hours && (
                          <p className="text-xs text-slate-500">
                            {lane.custom_hours.filter(h => h.is_open).length} days configured
                          </p>
                        )}
                      </div>
                    )}
                    
                    {!lane.use_custom_hours && (
                      <p className="text-xs text-slate-500">
                        Using business/season hours
                      </p>
                    )}
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={() => handleSaveLane(lane)} 
                      disabled={saving}
                      className="w-full bg-green-500 hover:bg-green-600"
                      data-testid={`save-lane-${lane.id}`}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save {lane.name} Settings
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            )
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LaneSettings;
